import { useState } from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, TextField, Alert, CircularProgress,
  Typography, LinearProgress, Divider,
} from '@mui/material'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { getOrder, listDeliveries, recordDelivery, type OrderListItem, type OrderLine } from '../../api/comenzi'
import { errorMessage } from '../../api/client'

export default function OrderDeliveriesDialog({ order, onClose }: { order: OrderListItem | null; onClose: () => void }) {
  const open = order !== null
  const { data: detail } = useQuery({
    queryKey: ['comercial', 'comanda', order?.id ?? null], queryFn: () => getOrder(order!.id), enabled: open,
  })

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Livrări — comandă {order?.orderNumber}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          {detail?.lines.map((l) => <LineDeliveries key={l.id} orderId={order!.id} line={l} />)}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Închide</Button>
      </DialogActions>
    </Dialog>
  )
}

function LineDeliveries({ orderId, line }: { orderId: string; line: OrderLine }) {
  const qc = useQueryClient()
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [qty, setQty] = useState('')
  const { data: deliveries } = useQuery({
    queryKey: ['comercial', 'comanda', orderId, 'livrari', line.id], queryFn: () => listDeliveries(orderId, line.id),
  })

  const percent = line.quantityThousands > 0 ? Math.min(100, (line.deliveredQuantity / line.quantityThousands) * 100) : 0

  const mutation = useMutation({
    mutationFn: () => recordDelivery(orderId, line.id, { deliveredDate: date, quantityThousands: Number(qty), notes: null }),
    onSuccess: () => {
      setQty('')
      void qc.invalidateQueries({ queryKey: ['comercial', 'comanda', orderId] })
    },
  })

  return (
    <Stack spacing={1}>
      <Stack direction="row" sx={{ justifyContent: 'space-between' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {line.articleCode} — {line.articleDescription}
          {line.requireCompleteSetDelivery && ' (set — livrare completă obligatorie)'}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {line.deliveredQuantity} / {line.quantityThousands} mii buc
        </Typography>
      </Stack>
      <LinearProgress variant="determinate" value={percent} />
      {deliveries && deliveries.length > 0 && (
        <Stack spacing={0.25}>
          {deliveries.map((d) => (
            <Typography key={d.id} variant="caption" color="text.secondary">
              {d.deliveredDate} — {d.quantityThousands} mii buc ({d.recordedByUserName})
            </Typography>
          ))}
        </Stack>
      )}
      {line.remainingQuantity > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
          <TextField type="date" size="small" value={date} onChange={(e) => setDate(e.target.value)} slotProps={{ inputLabel: { shrink: true } }} />
          <TextField type="number" size="small" label="Cantitate livrată" value={qty} onChange={(e) => setQty(e.target.value)} sx={{ width: 160 }} />
          <Button size="small" variant="outlined" disabled={!qty || mutation.isPending} onClick={() => mutation.mutate()}
            startIcon={mutation.isPending ? <CircularProgress size={14} /> : undefined}>
            Înregistrează livrare
          </Button>
        </Stack>
      )}
      {mutation.isError && <Alert severity="error">{errorMessage(mutation.error)}</Alert>}
      <Divider />
    </Stack>
  )
}
